import { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Trash2, AlertTriangle, ExternalLink, RefreshCw, ArrowUpRight, ArrowDownRight, Archive } from 'lucide-react';
import ModuleContainer from '../components/layout/ModuleContainer.jsx';
import { usePage } from '../contexts/PageContext.jsx';

// Sprint 21.18.1 (ADR-065): Polymarket Curation UI.
// Server-side Gamma discovery + user-curated tracked_markets management.

const VALID_CATEGORIES = ['fed_decision', 'cpi', 'geopolitical', 'corporate_event', 'election', 'other'];
const VALID_TIERS = ['core', 'active', 'monitor'];

const TIER_COLORS = {
  core: '#10b981',
  active: '#3b82f6',
  monitor: '#6b7280',
};

function fmtUsd(n) {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtPct(p) {
  if (!Number.isFinite(p)) return '—';
  return `${Math.round(p * 100)}¢`;
}

function fmtDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toISOString().slice(0, 10);
  } catch { return '—'; }
}

// ─── Discovery result card ─────────────────────────────────────────────

function DiscoverCard({ market, onAdd }) {
  const [adding, setAdding] = useState(false);
  const [stagedTier, setStagedTier] = useState(null);
  const [category, setCategory] = useState('other');
  const [tickers, setTickers] = useState('');
  const [sectors, setSectors] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const body = {
        slug: market.slug,
        tier: stagedTier,
        category,
        related_tickers: tickers.split(',').map(s => s.trim().toUpperCase()).filter(Boolean),
        related_sectors: sectors.split(',').map(s => s.trim().toUpperCase()).filter(Boolean),
        notes: notes.trim() || null,
      };
      const r = await fetch('/api/polymarket/markets/from-slug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`);
      setAdding(false);
      setStagedTier(null);
      onAdd && onAdd(data);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-3 py-2.5 bg-korinth-bg rounded border border-korinth-border/30">
      <p className="text-[12px] font-mono font-bold text-white leading-tight">{market.title}</p>
      <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[10px] font-mono">
        <span className="text-korinth-muted">slug:</span>
        <code className="text-korinth-text">{market.slug || '—'}</code>
        <span className="text-korinth-muted">·</span>
        <span className="text-korinth-muted">vol 24h <span className="text-white">{fmtUsd(market.volume_24h_usd)}</span></span>
        <span className="text-korinth-muted">·</span>
        <span className="text-korinth-muted">total <span className="text-white">{fmtUsd(market.volume_total_usd)}</span></span>
        <span className="text-korinth-muted">·</span>
        <span className="text-korinth-muted">liq <span className="text-white">{fmtUsd(market.liquidity_usd)}</span></span>
        <span className="text-korinth-muted">·</span>
        <span className="text-korinth-muted">ends <span className="text-white">{fmtDate(market.end_date)}</span></span>
      </div>
      <div className="flex items-center gap-2 mt-1 text-[10px] font-mono">
        <span className="text-korinth-muted">YES</span>
        <span className="text-korinth-accent font-bold">{fmtPct(market.yes_probability)}</span>
        <span className="text-korinth-muted">·</span>
        <span className="text-korinth-muted">NO</span>
        <span className="text-korinth-danger font-bold">{fmtPct(market.no_probability)}</span>
      </div>

      {!adding && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {VALID_TIERS.map(t => (
            <button
              key={t}
              type="button"
              onClick={() => { setStagedTier(t); setAdding(true); }}
              className="text-[10px] font-mono px-2 py-1 rounded font-bold uppercase tracking-wider hover:opacity-80"
              style={{ color: TIER_COLORS[t], backgroundColor: TIER_COLORS[t] + '20', border: `1px solid ${TIER_COLORS[t]}50` }}
            >
              + Add to {t}
            </button>
          ))}
          <span className="text-[8px] font-mono text-korinth-muted ml-auto">conditionId: {market.conditionId?.slice(0, 12)}…</span>
        </div>
      )}

      {adding && (
        <div className="mt-2 px-2 py-2 bg-korinth-surface/60 rounded border border-korinth-border/50 space-y-1.5">
          <div className="flex items-center gap-2 text-[10px] font-mono">
            <span className="text-korinth-muted">Adding to</span>
            <span className="px-1.5 py-0.5 rounded font-bold uppercase tracking-wider"
                  style={{ color: TIER_COLORS[stagedTier], backgroundColor: TIER_COLORS[stagedTier] + '20' }}>
              {stagedTier}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-0.5 text-[9px] font-mono text-korinth-muted">
              category
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="bg-korinth-bg border border-korinth-border/30 rounded px-2 py-1 text-white text-[10px] font-mono"
              >
                {VALID_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-0.5 text-[9px] font-mono text-korinth-muted">
              related tickers (comma-sep)
              <input
                type="text"
                value={tickers}
                onChange={(e) => setTickers(e.target.value)}
                placeholder="NVDA,SOXX"
                className="bg-korinth-bg border border-korinth-border/30 rounded px-2 py-1 text-white text-[10px] font-mono"
              />
            </label>
            <label className="flex flex-col gap-0.5 text-[9px] font-mono text-korinth-muted">
              related sectors (comma-sep)
              <input
                type="text"
                value={sectors}
                onChange={(e) => setSectors(e.target.value)}
                placeholder="XLK,SOXX"
                className="bg-korinth-bg border border-korinth-border/30 rounded px-2 py-1 text-white text-[10px] font-mono"
              />
            </label>
            <label className="flex flex-col gap-0.5 text-[9px] font-mono text-korinth-muted">
              notes
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="why this matters"
                className="bg-korinth-bg border border-korinth-border/30 rounded px-2 py-1 text-white text-[10px] font-mono"
              />
            </label>
          </div>
          {err && <p className="text-[10px] font-mono text-korinth-danger">{err}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="text-[10px] font-mono px-3 py-1 rounded bg-korinth-accent text-korinth-bg font-bold uppercase tracking-wider hover:opacity-80 disabled:opacity-40"
            >
              {busy ? 'saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => { setAdding(false); setErr(null); }}
              className="text-[10px] font-mono px-3 py-1 rounded border border-korinth-border/50 text-korinth-muted hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tracked-market card (compact) ─────────────────────────────────────

function TrackedCard({ market, onTierChange, onArchive, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const isPlaceholder = market.market_id === market.slug && (market.added_by === 'seed') && (market.snapshot_count === 0 || !market.latest_snapshot_at);

  const changeTier = async (newTier) => {
    setBusy(true);
    try {
      const r = await fetch(`/api/polymarket/markets/${encodeURIComponent(market.market_id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier: newTier }),
      });
      if (r.ok) onTierChange && onTierChange();
    } finally { setBusy(false); }
  };
  const archive = async () => {
    if (!window.confirm(`Archive market "${market.title}"?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/polymarket/markets/${encodeURIComponent(market.market_id)}`, { method: 'DELETE' });
      if (r.ok) onArchive && onArchive();
    } finally { setBusy(false); }
  };

  return (
    <div
      className="px-2 py-1.5 rounded border text-[10px] font-mono"
      style={{
        backgroundColor: isPlaceholder ? '#f59e0b15' : 'var(--korinth-bg, #0a0a0a)',
        borderColor: isPlaceholder ? '#f59e0b50' : 'rgba(255,255,255,0.1)',
      }}
    >
      <p className="text-white font-bold leading-tight truncate" title={market.title}>{market.title}</p>
      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
        <code className="text-[9px] text-korinth-muted truncate" title={market.slug}>{market.slug?.slice(0, 28) || '—'}{(market.slug?.length || 0) > 28 ? '…' : ''}</code>
        {isPlaceholder && (
          <span className="text-[8px] font-bold uppercase tracking-wider px-1 rounded" style={{ color: '#f59e0b', backgroundColor: '#f59e0b20' }}>
            placeholder
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-1 text-[9px] text-korinth-muted">
        <span>snapshots: <span className="text-white">{market.snapshot_count || 0}</span></span>
        {market.latest_yes_probability != null && (
          <span>YES <span className="text-white">{fmtPct(market.latest_yes_probability)}</span></span>
        )}
      </div>
      <div className="flex gap-1 mt-1.5">
        {VALID_TIERS.filter(t => t !== market.tier).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => changeTier(t)}
            disabled={busy}
            className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold hover:opacity-80 disabled:opacity-40"
            style={{ color: TIER_COLORS[t], backgroundColor: TIER_COLORS[t] + '15' }}
            title={`Move to ${t}`}
          >
            → {t}
          </button>
        ))}
        <button
          type="button"
          onClick={archive}
          disabled={busy}
          className="text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold hover:opacity-80 disabled:opacity-40 ml-auto"
          style={{ color: '#ef4444', backgroundColor: '#ef444415' }}
          title="Archive"
        >
          archive
        </button>
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────

export default function PolymarketCuratePage() {
  const { setPage } = usePage();
  useEffect(() => { setPage('polymarket-curate'); }, []);

  const [query, setQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  // Sprint 21.18.2: discovery sort mode. Defaults to 'relevance' so
  // topic searches surface topic-relevant markets, not always the
  // mega-liquid top-3.
  const [orderBy, setOrderBy] = useState('relevance');
  const [discoverResults, setDiscoverResults] = useState([]);
  const [discoverBusy, setDiscoverBusy] = useState(false);
  const [discoverError, setDiscoverError] = useState(null);

  const [tracked, setTracked] = useState({ core: [], active: [], monitor: [] });
  const [trackedTotal, setTrackedTotal] = useState(0);
  const [placeholderCount, setPlaceholderCount] = useState(0);
  const [freshness, setFreshness] = useState(null);

  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const reloadTracked = useCallback(async () => {
    try {
      const r = await fetch('/api/polymarket/markets');
      if (!r.ok) return;
      const data = await r.json();
      const byTier = { core: [], active: [], monitor: [] };
      let placeholders = 0;
      for (const m of (data.markets || [])) {
        if (m.status !== 'active') continue;
        const isPlaceholder = m.market_id === m.slug && (m.snapshot_count === 0 || !m.latest_snapshot_at);
        if (isPlaceholder) placeholders++;
        if (byTier[m.tier]) byTier[m.tier].push(m);
      }
      setTracked(byTier);
      setTrackedTotal((byTier.core.length + byTier.active.length + byTier.monitor.length));
      setPlaceholderCount(placeholders);
    } catch { /* tolerate */ }
    try {
      const r2 = await fetch('/api/polymarket/freshness');
      if (r2.ok) setFreshness(await r2.json());
    } catch { /* tolerate */ }
  }, []);

  useEffect(() => { reloadTracked(); }, [reloadTracked]);

  // Debounced discovery
  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setDiscoverResults([]);
      return;
    }
    const handle = setTimeout(async () => {
      setDiscoverBusy(true); setDiscoverError(null);
      try {
        const params = new URLSearchParams({
          q: query.trim(),
          limit: '20',
          active: String(activeOnly),
          order: orderBy,
        });
        const r = await fetch(`/api/polymarket/discover?${params}`);
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
        setDiscoverResults(data.markets || []);
      } catch (e) {
        setDiscoverError(e.message);
        setDiscoverResults([]);
      } finally {
        setDiscoverBusy(false);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [query, activeOnly, orderBy]);

  const runCleanup = async (confirm) => {
    setCleanupBusy(true);
    try {
      const r = await fetch('/api/polymarket/markets/cleanup-placeholders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: !!confirm }),
      });
      const data = await r.json();
      setCleanupResult(data);
      if (confirm && data.archived_count > 0) reloadTracked();
    } finally {
      setCleanupBusy(false);
    }
  };

  const freshColor = freshness?.freshness === 'fresh' ? '#10b981'
    : freshness?.freshness === 'stale' ? '#f59e0b'
    : '#ef4444';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Search className="w-6 h-6 text-korinth-accent" />
        <h1 className="text-2xl font-bold text-white">Polymarket Market Curation</h1>
        <span className="text-korinth-muted text-sm">ADR-049 user-curated · ADR-065 server-side Gamma</span>
      </div>

      {/* Stats strip */}
      <div className="bg-korinth-bg/50 border border-korinth-border/30 rounded-lg px-4 py-2.5 text-[11px] font-mono text-korinth-text flex flex-wrap items-center gap-4">
        <span><span className="text-korinth-muted">Tracked:</span> <span className="text-white font-bold">{trackedTotal}</span></span>
        <span style={{ color: TIER_COLORS.core }}>{tracked.core.length}c</span>
        <span style={{ color: TIER_COLORS.active }}>{tracked.active.length}a</span>
        <span style={{ color: TIER_COLORS.monitor }}>{tracked.monitor.length}m</span>
        {placeholderCount > 0 && (
          <span style={{ color: '#f59e0b' }}>
            ⚠ {placeholderCount} placeholder{placeholderCount === 1 ? '' : 's'} (no snapshots)
          </span>
        )}
        {freshness && (
          <span className="ml-auto" style={{ color: freshColor }}>
            data: {freshness.freshness} · last sync {freshness.age_minutes != null ? `${freshness.age_minutes}min ago` : 'pending'}
          </span>
        )}
        {placeholderCount > 0 && (
          <button
            type="button"
            onClick={() => runCleanup(false)}
            disabled={cleanupBusy}
            className="text-[10px] font-mono px-2 py-1 rounded font-bold uppercase tracking-wider hover:opacity-80 disabled:opacity-40"
            style={{ color: '#f59e0b', backgroundColor: '#f59e0b20', border: '1px solid #f59e0b50' }}
          >
            <Archive className="inline w-3 h-3 mr-1" />
            Bulk-archive placeholders…
          </button>
        )}
      </div>

      {/* Cleanup confirmation panel */}
      {cleanupResult && cleanupResult.dry_run && (
        <div className="bg-korinth-warning/10 border border-korinth-warning/30 rounded-lg px-4 py-3 space-y-2">
          <p className="text-[12px] font-mono text-korinth-warning font-bold">⚠ Found {cleanupResult.placeholder_count} placeholder seed(s) that never resolved to real Polymarket markets.</p>
          <p className="text-[10px] font-mono text-korinth-muted">These will be moved to status='archived'. Rows preserved for audit; will not appear in tracked list or be polled by sync cron.</p>
          <div className="text-[9px] font-mono text-korinth-muted max-h-32 overflow-y-auto bg-korinth-bg rounded p-2">
            {(cleanupResult.placeholder_slugs || []).map(s => (
              <div key={s.slug} className="truncate">[{s.tier}] {s.slug}</div>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => runCleanup(true)}
              disabled={cleanupBusy}
              className="text-[10px] font-mono px-3 py-1 rounded bg-korinth-warning text-korinth-bg font-bold uppercase tracking-wider hover:opacity-80 disabled:opacity-40"
            >
              {cleanupBusy ? 'archiving…' : `Archive ${cleanupResult.placeholder_count} placeholder${cleanupResult.placeholder_count === 1 ? '' : 's'}`}
            </button>
            <button
              type="button"
              onClick={() => setCleanupResult(null)}
              className="text-[10px] font-mono px-3 py-1 rounded border border-korinth-border/50 text-korinth-muted hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {cleanupResult && !cleanupResult.dry_run && cleanupResult.archived_count > 0 && (
        <div className="bg-korinth-accent/10 border border-korinth-accent/30 rounded-lg px-4 py-2 text-[11px] font-mono text-korinth-accent">
          ✓ Archived {cleanupResult.archived_count} placeholder{cleanupResult.archived_count === 1 ? '' : 's'}. Tracked list refreshed.
        </div>
      )}

      {/* Search panel */}
      <ModuleContainer title="🔍 Discover Polymarket Markets" subtitle="Server-side Gamma search · 5-min cache · 5 req/sec polite pacing (ADR-065)">
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Polymarket — e.g. bitcoin, fed cut, taiwan, NVDA earnings"
              className="flex-1 min-w-[280px] bg-korinth-bg border border-korinth-border/30 rounded px-3 py-2 text-white text-[12px] font-mono"
              autoFocus
            />
            <label className="flex items-center gap-1 text-[10px] font-mono text-korinth-muted">
              sort:
              <select
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value)}
                className="bg-korinth-bg border border-korinth-border/30 rounded px-2 py-1 text-white text-[10px] font-mono"
                title="Sprint 21.18.2: Relevance = Gamma's topic-match ranking; Volume/Liquidity = market size; Soonest = end-date ascending"
              >
                <option value="relevance">relevance</option>
                <option value="volume">volume</option>
                <option value="liquidity">liquidity</option>
                <option value="end_date">soonest</option>
              </select>
            </label>
            <label className="flex items-center gap-1 text-[10px] font-mono text-korinth-muted">
              <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
              active only
            </label>
            {discoverBusy && <RefreshCw className="w-4 h-4 text-korinth-muted animate-spin" />}
          </div>
          {discoverError && <p className="text-[10px] font-mono text-korinth-danger">⚠ {discoverError}</p>}
          {!query && (
            <p className="text-[10px] font-mono text-korinth-muted italic">Type a search term to discover real Polymarket markets. Each result shows the canonical Gamma-resolved record (conditionId, slug, volume, liquidity, current YES probability) — clicking "Add" inserts it into tracked_markets with the canonical conditionId, fixing the Sprint 21.15 placeholder-seed problem permanently.</p>
          )}
          {query && discoverResults.length === 0 && !discoverBusy && !discoverError && (
            <p className="text-[10px] font-mono text-korinth-muted italic">No results. Try a different query, or uncheck "active only" to include resolved markets.</p>
          )}
          <div className="space-y-2">
            {discoverResults.map(m => (
              <DiscoverCard key={m.conditionId || m.slug} market={m} onAdd={reloadTracked} />
            ))}
          </div>
        </div>
      </ModuleContainer>

      {/* Tracked markets — three columns */}
      <ModuleContainer title="📊 Currently Tracked Markets" subtitle="User-curated coverage by tier (ADR-049)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {VALID_TIERS.map(tier => {
            const list = tracked[tier] || [];
            return (
              <div key={tier}>
                <h3 className="text-[10px] font-mono uppercase tracking-wider mb-2 font-bold flex items-center gap-2"
                    style={{ color: TIER_COLORS[tier] }}>
                  {tier}
                  <span className="text-korinth-muted">({list.length})</span>
                </h3>
                <div className="space-y-1.5">
                  {list.length === 0 && <p className="text-[10px] font-mono text-korinth-muted italic">No markets in this tier.</p>}
                  {list.map(m => (
                    <TrackedCard key={m.market_id} market={m} onTierChange={reloadTracked} onArchive={reloadTracked} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[9px] font-mono text-korinth-muted text-center italic mt-4">
          Markets with snapshot_count=0 + market_id==slug + added_by='seed' are highlighted as <span style={{ color: '#f59e0b' }}>placeholder</span> — Sprint 21.15 seed slugs that never resolved to real Polymarket conditionIds. Use "Bulk-archive placeholders" to clear them, then re-curate via search above.
        </p>
      </ModuleContainer>
    </div>
  );
}
